import { useSetRecoilState } from 'recoil';

import { ShopOrderForSeller } from '@shared/api/shopOrder/shopOrder.interface';
import { Button } from '@shared/components/Button';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { UserRole } from '@shared/types';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';

import { OrderAlertResendModal } from './OrderAlertResendModal';
import { OrderGuestInfoEditModal } from './OrderGuestInfoEditModal';
import { OrderReceiverEditModal } from './OrderReceiverEditModal';

interface FlowerOrderInfoSummaryCardProps {
  shopOrder: ShopOrderForSeller;
  //* 주면 문구정보 옆에 [수정] 버튼이 붙는다 (모달은 호출한 카드가 소유)
  onPhraseEdit?: () => void;
}

export const ShopOrderDetailInfoCard = function ShopOrderDetailInfoCard({
  shopOrder,
  onPhraseEdit,
}: FlowerOrderInfoSummaryCardProps) {
  //* 게스트(임시고객) 주문일 때만 구매자 정보 수정 허용 (정회원 계정은 백엔드에서도 403)
  //* roles가 조회 안 된 쿼리에서는 일단 노출 — 백엔드 검증이 최종 보호막
  const guest = shopOrder.user;
  const isGuestOrder =
    typeof guest === 'object' &&
    Boolean(guest?._id) &&
    (guest.roles ? guest.roles.includes(UserRole.StoreGuest) : true);

  const guestEditOpenId = `order-guest-info-edit-${shopOrder._id}`;
  const setGuestEditOpen = useSetRecoilState(simpleOpenAtomFamily(guestEditOpenId));

  //* 알림 재발송 — 수정과 무관하게 독립 (상태별 가능 여부는 모달 안에서 disabled 처리)
  const alertResendOpenId = `order-alert-resend-${shopOrder._id}`;
  const setAlertResendOpen = useSetRecoilState(simpleOpenAtomFamily(alertResendOpenId));

  //* 수령인(상주) 교정 — 상가 상주 명단 선택 또는 직접 입력
  const receiverEditOpenId = `order-receiver-edit-${shopOrder._id}`;
  const setReceiverEditOpen = useSetRecoilState(simpleOpenAtomFamily(receiverEditOpenId));

  return (
    <div className="space-y-2 rounded-md border bg-white px-4 py-2 shadow-sm">
      <div className="flex w-full justify-between gap-4 border-b pb-2">
        <div className="w-1/2 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="min-w-0 break-keep text-sm font-bold">받으시는 분</p>
            <Button
              className="-my-1 h-6 px-1 shadow-sm"
              onClick={() => {
                setReceiverEditOpen(true);
              }}
            >
              수정
            </Button>
          </div>
          <div>
            <p className="min-w-0 flex-1 text-sm">
              {shopOrder.deliveryDetail.receiverName}
            </p>
            <p className="text-sm font-medium">
              {phoneNumberStringFormator(shopOrder.deliveryDetail.receiverPhoneNumber)}
            </p>
          </div>
        </div>
        <div className="w-1/2 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="min-w-0 break-keep text-sm font-bold">주문자</p>
            <div className="-my-1 flex gap-1">
              {isGuestOrder && (
                <Button
                  className="h-6 px-1 shadow-sm"
                  onClick={() => {
                    setGuestEditOpen(true);
                  }}
                >
                  수정
                </Button>
              )}
              <Button
                className="h-6 px-1 shadow-sm"
                onClick={() => {
                  setAlertResendOpen(true);
                }}
              >
                발송
              </Button>
            </div>
          </div>
          <div>
            <p className="min-w-0 flex-1 text-sm">
              {shopOrder.deliveryDetail.senderName}
            </p>
            <a href={`tel:${shopOrder.deliveryDetail.senderPhoneNumber}`}>
              <p className="text-sm font-medium">
                {phoneNumberStringFormator(shopOrder.deliveryDetail.senderPhoneNumber)}
              </p>
            </a>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-bold">문구정보</p>
          {onPhraseEdit && (
            <Button className="-my-1 h-6 px-1 shadow-sm" onClick={onPhraseEdit}>
              수정
            </Button>
          )}
        </div>
        <div className={`flex w-full justify-between gap-4 `}>
          <div className="w-1/2">
            <p className="pb-0.5 text-12 font-medium">보내는분(왼쪽)</p>
            <p className="text-sm">{shopOrder.bugoDetail.senderPhrase}</p>
          </div>
          <div className="w-1/2">
            <p className="pb-0.5 text-12 font-medium">조의문구(오른쪽)</p>
            <p className="text-sm">{shopOrder.bugoDetail.condolencePhrase}</p>
          </div>
        </div>
      </div>
      {isGuestOrder && (
        <OrderGuestInfoEditModal openId={guestEditOpenId} order={shopOrder} />
      )}
      <OrderAlertResendModal openId={alertResendOpenId} order={shopOrder} />
      <OrderReceiverEditModal openId={receiverEditOpenId} order={shopOrder} />
    </div>
  );
};
